import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useEvent, useUpdateEvent, useEventAdministrators, useCreateEventAdministrator, useUpdateEventAdministrator, useDeleteEventAdministrator, usePermissionGroups } from '@/lib/api/queries'
import { DEFAULT_EVENT_TIMEZONE, eventLocalToInstant, instantToEventLocal, timeZoneLabel } from '@/lib/eventTime'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useEffect, useState, useRef } from 'react'
import { cn } from '@/lib/utils'
import { usePermission } from '@/lib/permissions'
import { AccessDenied } from '@/components/permission-guard'
import { eventTakesCards } from '@/features/card-payments/labels'
import type { EventAdministrator, PermissionGroup } from '@/lib/api/types'
import { UserPlus, MoreVertical, Pencil, Trash2, Shield, X, Upload, ImageIcon } from 'lucide-react'

export const Route = createFileRoute('/_authenticated/events/$eventId/settings')({
  component: SettingsPage,
})

type SettingsTab = 'event-info' | 'payment-config' | 'branding' | 'notifications' | 'access'

function SettingsPage() {
  const { eventId } = Route.useParams()
  const eventIdNum = Number(eventId)
  const navigate = useNavigate()
  const formRef = useRef<HTMLFormElement>(null)
  
  // Permission check
  const canEditEvents = usePermission('can_edit_events')
  const canViewEventAdministrator = usePermission('view_eventadministrator')
  // The backend lets only staff who set up card readers turn cards on or off.
  const canManageCards = usePermission('can_manage_card_phones')
  
  const { data: event, isLoading } = useEvent(eventIdNum)
  // All date fields are venue wall-clock, anchored to the event timezone
  // (see src/lib/eventTime.ts) — never the admin's browser timezone. The
  // timezone select is controlled so the field labels always reflect the
  // zone the values will be interpreted in, even mid-edit; changing the
  // zone keeps the typed wall-clocks (re-anchors the stored instants).
  const [selectedTimeZone, setSelectedTimeZone] = useState('')
  const formTimeZone = selectedTimeZone || event?.timezone || DEFAULT_EVENT_TIMEZONE
  const dateZoneLabel = timeZoneLabel(formTimeZone)
  const updateEvent = useUpdateEvent()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<SettingsTab>('event-info')
  // The section on screen, for the sidebar. Every group but Access is one
  // scrolling page, so this follows the scroll.
  const [currentSection, setCurrentSection] = useState<string | null>(null)
  useEffect(() => {
    if (activeTab === 'access' || typeof IntersectionObserver === 'undefined') return
    const sections = [...document.querySelectorAll<HTMLElement>('[id^="settings-"]:not([id^="settings-group-"])')]
    if (sections.length === 0) return
    const visible = new Set<string>()
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) visible.add(entry.target.id)
        else visible.delete(entry.target.id)
      }
      // The topmost section in the band below the header.
      const first = sections.find((el) => visible.has(el.id))
      if (first) setCurrentSection(first.id)
    }, { rootMargin: '-96px 0px -55% 0px' })
    sections.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [activeTab, event?.id])
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)
  
  // File upload state for branding
  const [bannerFile, setBannerFile] = useState<File | null>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [bannerPreview, setBannerPreview] = useState<string | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const bannerInputRef = useRef<HTMLInputElement>(null)
  const logoInputRef = useRef<HTMLInputElement>(null)

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSaveError(null)
    setSaveSuccess(false)

    const formData = new FormData(e.currentTarget)
    
    // Build the update data object - only include non-empty values
    const data: Record<string, unknown> = {}
    
    // Event Info
    const name = formData.get('name') as string
    if (name) data.name = name
    
    const email = formData.get('email') as string
    if (email) data.email = email
    
    const timezone = formData.get('timezone') as string
    if (timezone) data.timezone = timezone

    const defaultLanguage = formData.get('default_language') as string
    if (defaultLanguage) data.default_language = defaultLanguage

    // Event Location - send all fields (allow clearing). Send null rather than ''
    // since these fields are null=True but not blank=True on the backend, so an
    // empty string would be rejected (notably `address`, which has no blank=True).
    data.venue_name = (formData.get('venue_name') as string)?.trim() || null
    data.address = (formData.get('address') as string)?.trim() || null
    data.city = (formData.get('city') as string)?.trim() || null
    data.zip_code = (formData.get('zip_code') as string)?.trim() || null
    data.country = (formData.get('country') as string)?.trim() || null

    // Event Dates — naive datetime-local values are venue wall-clock:
    // interpret them in the timezone selected on THIS form (so a timezone
    // change re-anchors the displayed wall-clocks), never the browser's.
    const dateTz = timezone || event?.timezone || DEFAULT_EVENT_TIMEZONE
    const startDateTime = formData.get('start_date_time') as string
    if (startDateTime) data.start_date_time = eventLocalToInstant(startDateTime, dateTz)
    
    const endDateTime = formData.get('end_date_time') as string
    if (endDateTime) data.end_date_time = eventLocalToInstant(endDateTime, dateTz)
    
    const publishStart = formData.get('publish_start_date_time') as string
    if (publishStart) data.publish_start_date_time = eventLocalToInstant(publishStart, dateTz)
    
    const publishEnd = formData.get('publish_end_date_time') as string
    if (publishEnd) data.publish_end_date_time = eventLocalToInstant(publishEnd, dateTz)
    
    const endTokenSales = formData.get('end_token_sales_date_time') as string
    if (endTokenSales) data.end_token_sales_date_time = eventLocalToInstant(endTokenSales, dateTz)
    
    const endTransfer = formData.get('end_transfer_tokens_date_time') as string
    if (endTransfer) data.end_transfer_tokens_date_time = eventLocalToInstant(endTransfer, dateTz)

    // Nullable: empty CLEARS the order cutoff (null = ordering never ends).
    const endOrders = formData.get('end_orders_date_time') as string
    data.end_orders_date_time = endOrders ? eventLocalToInstant(endOrders, dateTz) : null

    // Nullable by design: an empty field CLEARS the signup cutoff (the other
    // dates follow the legacy only-send-when-set pattern).
    const signupEnd = formData.get('signup_end_date_time') as string
    data.signup_end_date_time = signupEnd ? eventLocalToInstant(signupEnd, dateTz) : null
    
    // Vendor Permissions - checkboxes
    data.vendor_can_modify_item_price = formData.get('vendor_can_modify_item_price') === 'on'
    data.vendor_can_modify_item_details = formData.get('vendor_can_modify_item_details') === 'on'
    data.vendor_can_modify_item_availability = formData.get('vendor_can_modify_item_availability') === 'on'
    data.vendor_can_create_items = formData.get('vendor_can_create_items') === 'on'

    // Promotions - checkboxes
    data.enable_discount_schedules = formData.get('enable_discount_schedules') === 'on'
    data.vendor_can_create_discounts = formData.get('vendor_can_create_discounts') === 'on'
    
    // Token Configuration
    const tokenName = formData.get('token_name') as string
    if (tokenName) data.token_name = tokenName
    
    const tokenSymbol = formData.get('token_symbol') as string
    if (tokenSymbol) data.token_symbol = tokenSymbol
    
    const topupAmounts = formData.get('topup_amounts') as string
    if (topupAmounts) data.topup_amounts = topupAmounts
    
    const minTransactions = formData.get('min_transactions') as string
    if (minTransactions) data.min_transactions = parseInt(minTransactions) || 0
    
    const maxReloadAmount = formData.get('max_reload_amount') as string
    if (maxReloadAmount) data.max_reload_amount = parseFloat(maxReloadAmount) || null

    // Blank clears the event's own limit, so the system default applies again.
    const freeDirectPayMaxUnits = formData.get('free_direct_pay_max_units')
    if (freeDirectPayMaxUnits !== null) {
      const parsed = parseInt(freeDirectPayMaxUnits as string, 10)
      data.free_direct_pay_max_units = Number.isNaN(parsed) ? null : parsed
    }

    // Fees and Taxes
    const taxPercentage = formData.get('tax_percentage') as string
    if (taxPercentage) data.tax_percentage = parseFloat(taxPercentage) / 100 // Convert from display to decimal
    
    const taxPaidBy = formData.get('tax_paid_by') as string
    if (taxPaidBy) data.tax_paid_by = taxPaidBy
    
    const additionalFeePercentage = formData.get('additional_fee_percentage') as string
    if (additionalFeePercentage) data.additional_fee_percentage = parseFloat(additionalFeePercentage) / 100
    
    const additionalFeeFixed = formData.get('additional_fee_fixed') as string
    if (additionalFeeFixed) data.additional_fee_fixed = parseFloat(additionalFeeFixed) || 0
    
    const customPaymentText = formData.get('custom_payment_text') as string
    if (customPaymentText !== undefined) data.custom_payment_text = customPaymentText
    
    // Branding
    const accentColor = formData.get('accent_color') as string
    if (accentColor) data.accent_color = accentColor
    
    const accentTextColor = formData.get('accent_text_color') as string
    if (accentTextColor) data.accent_text_color = accentTextColor
    
    data.hide_logos_from_vendor_cards = formData.get('hide_logos_from_vendor_cards') === 'on'
    data.hide_vendor_event_name_from_vendor_page = formData.get('hide_vendor_event_name_from_vendor_page') === 'on'
    
    // Notifications
    data.show_notification = formData.get('show_notification') === 'on'
    
    const notificationText = formData.get('notification_text') as string
    if (notificationText !== undefined) data.notification_text = notificationText

    // Build files object if any images were selected
    const files: { banner?: File; logo?: File } = {}
    if (bannerFile) files.banner = bannerFile
    if (logoFile) files.logo = logoFile

    // Switching promotions on or off, or letting vendors create them, moves
    // menu prices and what each viewer may do on the Promotions page. Checked
    // on failure too: the fields can save before an image upload fails.
    const promotionsChanged =
      data.enable_discount_schedules !== (event?.enable_discount_schedules === true) ||
      data.vendor_can_create_discounts !== (event?.vendor_can_create_discounts === true)
    const refreshPromotions = () => {
      if (!promotionsChanged) return
      queryClient.invalidateQueries({ queryKey: ['menuItems'] })
      queryClient.invalidateQueries({ queryKey: ['menuItem'] })
      queryClient.invalidateQueries({ queryKey: ['item-discounts'] })
      queryClient.invalidateQueries({ queryKey: ['discounts', eventIdNum] })
    }

    try {
      await updateEvent.mutateAsync({ eventId: eventIdNum, data, files: (files.banner || files.logo) ? files : undefined })
      refreshPromotions()
      // Clear file state on success
      setBannerFile(null)
      setLogoFile(null)
      setBannerPreview(null)
      setLogoPreview(null)
      setSaveSuccess(true)
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' })
      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (error) {
      refreshPromotions()
      setSaveError(error instanceof Error ? error.message : 'Failed to update event settings')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }
  
  // Permission guard - must have edit events permission to access settings
  if (!canEditEvents) {
    return <AccessDenied message="You don't have permission to access event settings." />
  }

  if (isLoading) {
    return (
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-neutral-200 dark:bg-neutral-800 rounded w-1/4" />
          <div className="h-12 bg-neutral-200 dark:bg-neutral-800 rounded" />
          <div className="h-64 bg-neutral-200 dark:bg-neutral-800 rounded" />
        </div>
      </div>
    )
  }

  // A Direct Pay event charges in currency: its token settings are its payment setup.
  const tokensLabel = event?.event_type === 'direct_pay' ? 'Payment Configuration' : 'Tokens'

  // The sidebar: each group is a tab, its sections jump within it.
  const tabs: {
    key: SettingsTab; label: string; icon: React.ReactNode; permission?: boolean
    sections?: { id: string; label: string }[]
  }[] = [
    {
      key: 'event-info',
      label: 'Event Info',
      sections: [
        { id: 'settings-information', label: 'Information' },
        { id: 'settings-location', label: 'Location' },
        { id: 'settings-dates', label: 'Dates' },
        { id: 'settings-vendor-permissions', label: 'Vendor permissions' },
        { id: 'settings-promotions', label: 'Promotions' },
      ],
      icon: (
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14,2 14,8 20,8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
        </svg>
      ),
    },
    {
      key: 'payment-config',
      label: 'Payments',
      sections: [
        { id: 'settings-tokens', label: tokensLabel },
        { id: 'settings-fees', label: 'Fees and Taxes' },
        ...(event && eventTakesCards(event) ? [{ id: 'settings-card-payments', label: 'Card Payments' }] : []),
      ],
      icon: (
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
          <line x1="1" y1="10" x2="23" y2="10"></line>
        </svg>
      ),
    },
    {
      key: 'branding',
      label: 'Branding',
      sections: [
        { id: 'settings-images', label: 'Images' },
        { id: 'settings-display', label: 'Display' },
      ],
      icon: (
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <circle cx="9" cy="9" r="2"></circle>
          <path d="M21 15l-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
        </svg>
      ),
    },
    {
      key: 'notifications',
      label: 'Notifications',
      sections: [{ id: 'settings-notifications', label: 'Notifications' }],
      icon: (
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"></path>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
        </svg>
      ),
    },
    {
      key: 'access',
      label: 'Access',
      icon: (
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="8.5" cy="7" r="4"></circle>
          <polyline points="17 11 19 13 23 9"></polyline>
        </svg>
      ),
      permission: canViewEventAdministrator,
    },
  ]

  const visibleTabs = tabs.filter(tab => tab.permission === undefined || tab.permission)

  // Hash to tab mapping
  const hashTabMap: Record<string, SettingsTab> = {
    overview: 'event-info',
    payments: 'payment-config',
    branding: 'branding',
    notifications: 'notifications',
    access: 'access',
  }
  const tabHashMap: Record<SettingsTab, string> = {
    'event-info': 'overview',
    'payment-config': 'payments',
    branding: 'branding',
    notifications: 'notifications',
    access: 'access',
  }

  // Read hash on mount only (no listeners, no continuous sync)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const hash = window.location.hash.replace('#', '')
    const nextTab = hashTabMap[hash]
    if (nextTab && nextTab !== activeTab) {
      setActiveTab(nextTab)
    }
    if (nextTab && nextTab !== 'access' && nextTab !== 'event-info') scrollToId(`settings-group-${nextTab}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Update hash when clicking a group (not in useEffect to avoid loops):
  // Access is its own view, every other group a place on the one page.
  const handleTabClick = (tab: SettingsTab) => {
    setActiveTab(tab)
    if (typeof window !== 'undefined') {
      const newHash = `#${tabHashMap[tab]}`
      window.history.replaceState(null, '', newHash)
    }
    if (tab !== 'access') scrollToId(`settings-group-${tab}`)
  }

  const handleSectionClick = (tab: SettingsTab, sectionId: string) => {
    setActiveTab(tab)
    if (typeof window !== 'undefined') window.history.replaceState(null, '', `#${tabHashMap[tab]}`)
    scrollToId(sectionId)
  }


  return (
    <div className="max-w-[1200px] mx-auto px-4 md:px-8">
      <div className="min-h-[calc(100vh-8rem)] flex flex-col py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">Event Settings</h1>
          <p className="text-neutral-600 dark:text-neutral-400">Configure settings for {event?.name}</p>
        </div>

        <div className="flex flex-col md:flex-row gap-6 md:gap-8 grow">
        {/* Sidebar: the page's contents; a dropdown on a phone */}
        <aside className="md:w-56 shrink-0">
          <select
            aria-label="Settings section"
            className="md:hidden w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={activeTab === 'access' ? 'access' : currentSection ?? ''}
            onChange={(e) => {
              const id = e.target.value
              if (id === 'access') return handleTabClick('access')
              const tab = visibleTabs.find((t) => (t.sections ?? []).some((section) => section.id === id))
              if (tab) handleSectionClick(tab.key, id)
            }}
          >
            {visibleTabs.map((tab) => (tab.sections && tab.sections.length > 0 ? (
              <optgroup key={tab.key} label={tab.label}>
                {tab.sections.map((section) => <option key={section.id} value={section.id}>{section.label}</option>)}
              </optgroup>
            ) : (
              <option key={tab.key} value={tab.key === 'access' ? 'access' : `settings-${tab.key}`}>{tab.label}</option>
            )))}
          </select>
          <nav className="hidden md:block sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto space-y-4 pb-4">
            {visibleTabs.map((tab) => (
              <div key={tab.key}>
                <button
                  type="button"
                  onClick={() => handleTabClick(tab.key)}
                  className={cn(
                    'flex items-center w-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wide rounded-md',
                    tab.key === 'access' && activeTab === 'access'
                      ? 'bg-neutral-100 dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100'
                      : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100'
                  )}
                >
                  {tab.icon}
                  {tab.label}
                </button>
                {tab.sections && tab.sections.length > 0 && (
                  <ul className="mt-0.5 space-y-0.5">
                    {tab.sections.map((section) => {
                      const current = activeTab !== 'access' && currentSection === section.id
                      return (
                        <li key={section.id}>
                          <button
                            type="button"
                            onClick={() => handleSectionClick(tab.key, section.id)}
                            className={cn(
                              'w-full text-left pl-9 pr-3 py-1.5 text-sm rounded-md border-l-2 transition-colors',
                              current
                                ? 'border-blue-600 bg-neutral-100 dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 font-medium'
                                : 'border-transparent text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100'
                            )}
                          >
                            {section.label}
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            ))}
          </nav>
        </aside>

        <div className="flex-1 min-w-0 flex flex-col space-y-6">
        {/* Success/Error Messages */}
        {saveSuccess && (
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-800 dark:text-green-200 text-sm">
            Event settings updated successfully!
          </div>
        )}
        {saveError && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-200 text-sm">
            {saveError}
          </div>
        )}

        {/* Tab Content */}
        {activeTab !== 'access' && (
          <form ref={formRef} className="space-y-8 grow" onSubmit={handleSubmit}>
            {/* Event Info Tab */}
            <div id="settings-group-event-info" className="scroll-mt-24 space-y-8">
            {/* Event Information */}
              <div id="settings-information" className="scroll-mt-24 bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-6">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Event Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Event Name
                    </label>
                    <Input
                      name="name"
                      type="text"
                      defaultValue={event?.name}
                      className="w-full"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Contact Email
                    </label>
                    <Input
                      name="email"
                      type="email"
                      defaultValue={event?.email}
                      className="w-full"
                    />
                  </div>

                  {/* Timezone */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Timezone
                    </label>
                    <select
                      name="timezone"
                      value={formTimeZone}
                      onChange={(e) => setSelectedTimeZone(e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select timezone...</option>
                      <option value="America/Toronto">America/Toronto (Eastern)</option>
                      <option value="America/Vancouver">America/Vancouver (Pacific)</option>
                      <option value="America/New_York">America/New_York (Eastern)</option>
                      <option value="America/Los_Angeles">America/Los_Angeles (Pacific)</option>
                      <option value="America/Chicago">America/Chicago (Central)</option>
                      <option value="America/Denver">America/Denver (Mountain)</option>
                      <option value="UTC">UTC</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Event Location */}
              <div id="settings-location" className="scroll-mt-24 bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-6">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Event Location</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Venue Name */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Venue Name
                    </label>
                    <Input
                      name="venue_name"
                      type="text"
                      defaultValue={event?.venue_name}
                      placeholder="e.g. Celebration Square"
                      className="w-full"
                    />
                  </div>

                  {/* Default Language */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Default Language
                    </label>
                    <select
                      name="default_language"
                      defaultValue={event?.default_language || 'en'}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="en">English</option>
                      <option value="fr-ca">French (CA)</option>
                      <option value="es">Spanish</option>
                    </select>
                  </div>

                  {/* Address */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Address
                    </label>
                    <Input
                      name="address"
                      type="text"
                      defaultValue={event?.address}
                      placeholder="Street address"
                      className="w-full"
                    />
                  </div>

                  {/* City */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      City
                    </label>
                    <Input
                      name="city"
                      type="text"
                      defaultValue={event?.city}
                      placeholder="City"
                      className="w-full"
                    />
                  </div>

                  {/* Postal/Zip */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Postal/Zip
                    </label>
                    <Input
                      name="zip_code"
                      type="text"
                      defaultValue={event?.zip_code}
                      placeholder="Postal / Zip code"
                      className="w-full"
                    />
                  </div>

                  {/* Country */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Country
                    </label>
                    <Input
                      name="country"
                      type="text"
                      defaultValue={event?.country}
                      placeholder="Country"
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Event Dates */}
              <div id="settings-dates" className="scroll-mt-24 bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-6">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Event Dates</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Start Date/Time */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Start Date/Time <span className="font-normal text-neutral-400">({dateZoneLabel})</span>
                    </label>
                    <Input
                      name="start_date_time"
                      type="datetime-local"
                      defaultValue={instantToEventLocal(event?.start_date_time, formTimeZone)}
                      className="w-full"
                    />
                  </div>

                  {/* End Date/Time */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      End Date/Time <span className="font-normal text-neutral-400">({dateZoneLabel})</span>
                    </label>
                    <Input
                      name="end_date_time"
                      type="datetime-local"
                      defaultValue={instantToEventLocal(event?.end_date_time, formTimeZone)}
                      className="w-full"
                    />
                  </div>

                  {/* Publish Start Date/Time */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Publish Start Date/Time <span className="font-normal text-neutral-400">({dateZoneLabel})</span>
                    </label>
                    <Input
                      name="publish_start_date_time"
                      type="datetime-local"
                      defaultValue={instantToEventLocal(event?.publish_start_date_time, formTimeZone)}
                      className="w-full"
                    />
                  </div>

                  {/* Publish End Date/Time */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Publish End Date/Time <span className="font-normal text-neutral-400">({dateZoneLabel})</span>
                    </label>
                    <Input
                      name="publish_end_date_time"
                      type="datetime-local"
                      defaultValue={instantToEventLocal(event?.publish_end_date_time, formTimeZone)}
                      className="w-full"
                    />
                  </div>

                  {/* End Token Sales Date/Time */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      End Token Sales Date/Time <span className="font-normal text-neutral-400">({dateZoneLabel})</span>
                    </label>
                    <Input
                      name="end_token_sales_date_time"
                      type="datetime-local"
                      defaultValue={instantToEventLocal(event?.end_token_sales_date_time, formTimeZone)}
                      className="w-full"
                    />
                  </div>

                  {/* End Transfer Tokens Date/Time */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      End Transfer Tokens Date/Time <span className="font-normal text-neutral-400">({dateZoneLabel})</span>
                    </label>
                    <Input
                      name="end_transfer_tokens_date_time"
                      type="datetime-local"
                      defaultValue={instantToEventLocal(event?.end_transfer_tokens_date_time, formTimeZone)}
                      className="w-full"
                    />
                  </div>

                  {/* End Orders Date/Time */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      End Orders Date/Time <span className="font-normal text-neutral-400">({dateZoneLabel})</span>
                    </label>
                    <Input
                      name="end_orders_date_time"
                      type="datetime-local"
                      defaultValue={instantToEventLocal(event?.end_orders_date_time, formTimeZone)}
                      className="w-full"
                    />
                    <p className="mt-1.5 text-xs text-neutral-500 dark:text-neutral-400">
                      After this time, new orders (token and direct-pay) are rejected. Leave empty for no cutoff.
                    </p>
                  </div>

                  {/* Signup End Date/Time */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Signup End Date/Time <span className="font-normal text-neutral-400">({dateZoneLabel})</span>
                    </label>
                    <Input
                      name="signup_end_date_time"
                      type="datetime-local"
                      defaultValue={instantToEventLocal(event?.signup_end_date_time, formTimeZone)}
                      className="w-full"
                    />
                    <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                      New attendees can't join the event after this time. Leave empty for no cutoff.
                    </p>
                  </div>
                </div>
              </div>

              {/* Vendor Permissions */}
              <div id="settings-vendor-permissions" className="scroll-mt-24 bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-6">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">Vendor Permissions</h2>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                  Control what vendors can modify for their menu items. These settings apply to Vendor Administrators and Vendor Event Administrators.
                </p>
                <div className="space-y-4">
                  {/* Vendor Can Modify Item Price */}
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="vendor_can_modify_item_price"
                      name="vendor_can_modify_item_price"
                      defaultChecked={event?.vendor_can_modify_item_price !== false}
                      className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-600 rounded"
                    />
                    <label htmlFor="vendor_can_modify_item_price" className="ml-3 block text-sm text-neutral-700 dark:text-neutral-300">
                      <span className="font-medium">Allow vendors to modify item prices</span>
                      <span className="block text-neutral-500 dark:text-neutral-400 text-xs mt-0.5">
                        Vendors can change the token price and discount price of their menu items
                      </span>
                    </label>
                  </div>

                  {/* Vendor Can Modify Item Details */}
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="vendor_can_modify_item_details"
                      name="vendor_can_modify_item_details"
                      defaultChecked={event?.vendor_can_modify_item_details !== false}
                      className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-600 rounded"
                    />
                    <label htmlFor="vendor_can_modify_item_details" className="ml-3 block text-sm text-neutral-700 dark:text-neutral-300">
                      <span className="font-medium">Allow vendors to modify item details</span>
                      <span className="block text-neutral-500 dark:text-neutral-400 text-xs mt-0.5">
                        Vendors can change the name, description, and dietary options of their menu items
                      </span>
                    </label>
                  </div>

                  {/* Vendor Can Modify Item Availability */}
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="vendor_can_modify_item_availability"
                      name="vendor_can_modify_item_availability"
                      defaultChecked={event?.vendor_can_modify_item_availability !== false}
                      className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-600 rounded"
                    />
                    <label htmlFor="vendor_can_modify_item_availability" className="ml-3 block text-sm text-neutral-700 dark:text-neutral-300">
                      <span className="font-medium">Allow vendors to modify item availability</span>
                      <span className="block text-neutral-500 dark:text-neutral-400 text-xs mt-0.5">
                        Vendors can toggle the availability status and visibility of their menu items
                      </span>
                    </label>
                  </div>

                  {/* Vendor Can Create Items */}
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="vendor_can_create_items"
                      name="vendor_can_create_items"
                      defaultChecked={event?.vendor_can_create_items !== false}
                      className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-600 rounded"
                    />
                    <label htmlFor="vendor_can_create_items" className="ml-3 block text-sm text-neutral-700 dark:text-neutral-300">
                      <span className="font-medium">Allow vendors to create items</span>
                      <span className="block text-neutral-500 dark:text-neutral-400 text-xs mt-0.5">
                        Vendors can add items to their menus and set their price, and delete items, even with the options above turned off
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Promotions */}
              <div id="settings-promotions" className="scroll-mt-24 bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-6">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">Promotions</h2>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4">
                  Scheduled discounts on menu items. Build them on the Promotions page.
                </p>
                <div className="space-y-4">
                  {/* Enable Discount Schedules */}
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="enable_discount_schedules"
                      name="enable_discount_schedules"
                      defaultChecked={event?.enable_discount_schedules === true}
                      className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-600 rounded"
                    />
                    <label htmlFor="enable_discount_schedules" className="ml-3 block text-sm text-neutral-700 dark:text-neutral-300">
                      <span className="font-medium">Enable discount schedules</span>
                      <span className="block text-neutral-500 dark:text-neutral-400 text-xs mt-0.5">
                        When on, promotions on the Promotions page change what customers are charged. Off leaves every price as it is today.
                      </span>
                    </label>
                  </div>

                  {/* Vendors Can Create Promotions */}
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="vendor_can_create_discounts"
                      name="vendor_can_create_discounts"
                      defaultChecked={event?.vendor_can_create_discounts === true}
                      className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-600 rounded"
                    />
                    <label htmlFor="vendor_can_create_discounts" className="ml-3 block text-sm text-neutral-700 dark:text-neutral-300">
                      <span className="font-medium">Vendors can create promotions</span>
                      <span className="block text-neutral-500 dark:text-neutral-400 text-xs mt-0.5">
                        Vendor and location admins with the edit-promotions permission can create schedules for their own menus. Event admins are not affected.
                      </span>
                    </label>
                  </div>
                </div>
              </div>
          </div>

          {/* Payment Config Tab */}
          <div id="settings-group-payment-config" className="scroll-mt-24 space-y-8">
              {/* Tokens (Payment Configuration on a Direct Pay event) */}
              <div id="settings-tokens" className="scroll-mt-24 bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-6">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">{tokensLabel}</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Currency */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Currency
                    </label>
                    <select
                      disabled
                      defaultValue={event?.currency || 'CAD'}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-neutral-100 dark:bg-neutral-900 text-neutral-500 cursor-not-allowed opacity-60"
                    >
                      <option value="CAD">CAD - Canadian Dollar</option>
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="GBP">GBP - British Pound</option>
                    </select>
                  </div>

                  {/* Token Value */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Token Value
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      disabled
                      defaultValue={event?.token_value}
                      className="bg-neutral-100 dark:bg-neutral-900 text-neutral-500 cursor-not-allowed opacity-60"
                    />
                  </div>

                  {/* Event Type */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Event Type
                    </label>
                    <select
                      disabled
                      defaultValue={event?.event_type || 'token'}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-neutral-100 dark:bg-neutral-900 text-neutral-500 cursor-not-allowed opacity-60"
                    >
                      <option value="token">Token Event</option>
                      <option value="direct_pay">Direct Pay Event</option>
                      <option value="sampling">Sampling Event</option>
                    </select>
                  </div>

                  {/* Token Name */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Token Name
                    </label>
                    <Input 
                      name="token_name"
                      type="text" 
                      defaultValue={event?.token_name}
                      className="w-full" 
                    />
                  </div>

                  {/* Token Symbol */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Token Symbol
                    </label>
                    <Input 
                      name="token_symbol"
                      type="text" 
                      defaultValue={event?.token_symbol}
                      className="w-full" 
                    />
                  </div>

                  {/* Topup Amounts */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Topup Amounts (Tokens)
                    </label>
                    <Input 
                      name="topup_amounts"
                      type="text" 
                      defaultValue={event?.topup_amounts}
                      placeholder="10,20,30,50" 
                      className="w-full" 
                    />
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Separate with commas (e.g., 10,20,30,50)</p>
                  </div>

                  {/* Minimum Reload Amount */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Minimum Reload Amount (Tokens)
                    </label>
                    <Input 
                      name="min_transactions"
                      type="number" 
                      min="0"
                      defaultValue={event?.min_transactions}
                      className="w-full" 
                    />
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">The minimum number of tokens per reload</p>
                  </div>

                  {/* Maximum Reload Amount */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Maximum Reload Amount (Tokens)
                    </label>
                    <Input 
                      name="max_reload_amount"
                      type="number" 
                      min="0"
                      step="0.01"
                      defaultValue={event?.max_reload_amount}
                      className="w-full" 
                    />
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">The maximum number of tokens per reload. Leave blank to remove the limit.</p>
                  </div>

                  {/* Free Direct Pay order size */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Maximum Items per Free Direct Pay Order
                    </label>
                    <Input
                      name="free_direct_pay_max_units"
                      type="number"
                      min="0"
                      step="1"
                      defaultValue={event?.free_direct_pay_max_units ?? ''}
                      className="w-full"
                    />
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Items allowed in one order of free Direct Pay items (no card charge). Leave blank for the system default.</p>
                  </div>
                </div>
              </div>

              {/* Fees and Taxes */}
              <div id="settings-fees" className="scroll-mt-24 bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-6">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Fees and Taxes</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Tax Percentage */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Tax Percentage
                    </label>
                    <div className="relative">
                      <Input
                        name="tax_percentage"
                        type="number"
                        step="0.001"
                        min="0"
                        max="100"
                        // Up to 3 decimals (e.g. Quebec 14.975), trailing zeros trimmed.
                        defaultValue={event?.tax_percentage ? String(parseFloat((event.tax_percentage * 100).toFixed(3))) : ''}
                        placeholder="7.0"
                        className="w-full pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500">%</span>
                    </div>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Enter as percentage (e.g., 13 for 13%)</p>
                  </div>

                  {/* Tax Paid By */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Tax Paid By
                    </label>
                    <select 
                      name="tax_paid_by"
                      defaultValue={event?.tax_paid_by || 'user'}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="user">User</option>
                      <option value="event">Event</option>
                    </select>
                  </div>

                  {/* Additional Fee Percentage */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Additional Fee Percentage
                    </label>
                    <div className="relative">
                      <Input
                        name="additional_fee_percentage"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        // Field is only present when set; 0/absent both show 0 (nullish,
                        // not truthy — a truthy check hides a real 0). Trailing zeros trimmed.
                        defaultValue={String(parseFloat(((event?.additional_fee_percentage ?? 0) * 100).toFixed(2)))}
                        placeholder="2.5"
                        className="w-full pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500">%</span>
                    </div>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Enter as percentage (e.g., 2.5 for 2.5%)</p>
                  </div>

                  {/* Additional Fee Fixed */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Additional Fee Fixed
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500">$</span>
                      <Input
                        name="additional_fee_fixed"
                        type="number"
                        step="0.01"
                        min="0"
                        // Only present when set; missing/0 both show 0.
                        defaultValue={event?.additional_fee_fixed ?? 0}
                        className="w-full pl-8"
                      />
                    </div>
                  </div>

                  {/* Custom Payment Text */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Custom Payment Text
                    </label>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-2">
                      This will appear on the Add Tokens page and in the email sent to users when they add tokens.
                    </p>
                    <textarea
                      name="custom_payment_text"
                      rows={3}
                      defaultValue={event?.custom_payment_text}
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Card Payments: switched on the Card payments page */}
              {event && eventTakesCards(event) && (
                <div id="settings-card-payments" className="scroll-mt-24 bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-6">
                  <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">Card Payments</h2>
                  <p className="text-sm text-neutral-700 dark:text-neutral-300">
                    {event.card_payments_enabled ? 'On' : 'Off'} for this event.
                    {' '}Card payments, each location's setting and the phones that take cards are on{' '}
                    {canManageCards ? (
                      <Link to="/events/$eventId/card-payments" params={{ eventId }} className="text-blue-600 dark:text-blue-400 hover:underline">
                        Card payments
                      </Link>
                    ) : 'Card payments'}.
                    {!canManageCards && ' That page needs card reader setup access.'}
                  </p>
                </div>
              )}
          </div>

          {/* Branding Tab */}
          <div id="settings-group-branding" className="scroll-mt-24 space-y-8">
            {/* Images */}
              <div id="settings-images" className="scroll-mt-24 bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-6">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Images</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Event Banner */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Event Banner
                    </label>
                    {(bannerPreview || event?.banner) ? (
                      <div className="mb-3 relative group">
                        <img
                          src={bannerPreview || event?.banner}
                          alt="Event banner"
                          className="w-full h-40 object-cover rounded-lg border border-neutral-200 dark:border-neutral-700"
                        />
                        {bannerPreview && (
                          <span className="absolute top-2 left-2 px-2 py-0.5 text-xs font-medium bg-blue-600 text-white rounded">New</span>
                        )}
                      </div>
                    ) : (
                      <div className="mb-3 w-full h-40 bg-neutral-100 dark:bg-neutral-800 rounded-lg border-2 border-dashed border-neutral-300 dark:border-neutral-700 flex flex-col items-center justify-center">
                        <ImageIcon className="w-10 h-10 text-neutral-400 dark:text-neutral-600 mb-2" />
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">No banner uploaded</p>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => bannerInputRef.current?.click()}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-neutral-300 dark:border-neutral-700 rounded-lg text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                      {(bannerPreview || event?.banner) ? 'Change Banner' : 'Upload Banner'}
                    </button>
                    <input
                      ref={bannerInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) {
                          setBannerFile(file)
                          const reader = new FileReader()
                          reader.onload = (ev) => setBannerPreview(ev.target?.result as string)
                          reader.readAsDataURL(file)
                        }
                      }}
                      className="hidden"
                    />
                  </div>

                  {/* Event Logo */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Event Logo
                    </label>
                    {(logoPreview || event?.logo) ? (
                      <div className="mb-3 relative group">
                        <div className="w-full h-40 bg-neutral-50 dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 flex items-center justify-center p-4">
                          <img
                            src={logoPreview || event?.logo}
                            alt="Event logo"
                            className="max-w-full max-h-32 object-contain"
                          />
                        </div>
                        {logoPreview && (
                          <span className="absolute top-2 left-2 px-2 py-0.5 text-xs font-medium bg-blue-600 text-white rounded">New</span>
                        )}
                      </div>
                    ) : (
                      <div className="mb-3 w-full h-40 bg-neutral-100 dark:bg-neutral-800 rounded-lg border-2 border-dashed border-neutral-300 dark:border-neutral-700 flex flex-col items-center justify-center">
                        <ImageIcon className="w-10 h-10 text-neutral-400 dark:text-neutral-600 mb-2" />
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">No logo uploaded</p>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-neutral-300 dark:border-neutral-700 rounded-lg text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                    >
                      <Upload className="w-4 h-4" />
                      {(logoPreview || event?.logo) ? 'Change Logo' : 'Upload Logo'}
                    </button>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) {
                          setLogoFile(file)
                          const reader = new FileReader()
                          reader.onload = (ev) => setLogoPreview(ev.target?.result as string)
                          reader.readAsDataURL(file)
                        }
                      }}
                      className="hidden"
                    />
                  </div>
                </div>
              </div>

              {/* Display Options */}
              <div id="settings-display" className="scroll-mt-24 bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-6">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Display Options</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Accent Color */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Accent Color
                    </label>
                    <div className="flex gap-3">
                      <input 
                        type="color" 
                        defaultValue={event?.accent_color || '#242424'} 
                        className="w-16 h-10 rounded-md border border-neutral-300 dark:border-neutral-600" 
                      />
                      <Input 
                        name="accent_color"
                        type="text" 
                        defaultValue={event?.accent_color || '#242424'} 
                        placeholder="#242424"
                        maxLength={7}
                        className="flex-1 font-mono text-sm" 
                      />
                    </div>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Enter hex color code (e.g., #242424)</p>
                  </div>

                  {/* Accent Text Color */}
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Accent Text Color
                    </label>
                    <div className="flex gap-3">
                      <input 
                        type="color" 
                        defaultValue={event?.accent_text_color || '#ffffff'} 
                        className="w-16 h-10 rounded-md border border-neutral-300 dark:border-neutral-600" 
                      />
                      <Input 
                        name="accent_text_color"
                        type="text" 
                        defaultValue={event?.accent_text_color || '#ffffff'} 
                        placeholder="#ffffff"
                        maxLength={7}
                        className="flex-1 font-mono text-sm" 
                      />
                    </div>
                    <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Enter hex color code (e.g., #ffffff)</p>
                  </div>
                </div>

                <div className="mt-2 space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="hide_logos_from_vendor_cards"
                      name="hide_logos_from_vendor_cards"
                      defaultChecked={event?.hide_logos_from_vendor_cards}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-600 rounded"
                    />
                    <label htmlFor="hide_logos_from_vendor_cards" className="ml-2 block text-sm text-neutral-700 dark:text-neutral-300">
                      Vendor List: Hide Logos
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="hide_vendor_event_name_from_vendor_page"
                      name="hide_vendor_event_name_from_vendor_page"
                      defaultChecked={event?.hide_vendor_event_name_from_vendor_page}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-600 rounded"
                    />
                    <label htmlFor="hide_vendor_event_name_from_vendor_page" className="ml-2 block text-sm text-neutral-700 dark:text-neutral-300">
                      Vendor Page: Hide Vendor Name
                    </label>
                  </div>
                </div>
              </div>
          </div>

          {/* Notifications Tab */}
          <div id="settings-group-notifications" className="scroll-mt-24 space-y-8">
            <div id="settings-notifications" className="scroll-mt-24 bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-6">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Notifications</h2>
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="show_notification"
                    name="show_notification"
                    defaultChecked={event?.show_notification}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-600 rounded"
                  />
                  <label htmlFor="show_notification" className="ml-2 block text-sm text-neutral-700 dark:text-neutral-300">
                    Show notification to users
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Notification Text
                  </label>
                  <textarea
                    name="notification_text"
                    rows={3}
                    defaultValue={event?.notification_text}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>


            {/* Action Buttons - pinned to the bottom of the page */}
            <div className="sticky bottom-0 z-10 flex justify-end space-x-4 py-3 border-t border-neutral-200 dark:border-neutral-800 bg-white/95 dark:bg-[#131313]/95 backdrop-blur">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate({ to: `/events/${eventId}/dashboard` })}
              >
                Cancel
              </Button>
              <Button type="submit" isLoading={updateEvent.isPending}>
                Update Event Settings
              </Button>
            </div>
          </form>
        )}
        
        {/* Access Tab - rendered outside the form to avoid nested forms */}
        {canViewEventAdministrator && (
          <div className={activeTab !== 'access' ? 'hidden' : ''}>
            <EventAccessTab eventId={eventIdNum} />
          </div>
        )}
        </div>
        </div>
      </div>

    </div>
  )
}

/** Scroll to an element once the next render is on screen (the form mounts
 *  again when leaving Access). */
function scrollToId(id: string) {
  requestAnimationFrame(() => requestAnimationFrame(() => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }))
}

// Event Access Tab Component
function EventAccessTab({ eventId }: { eventId: number }) {
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingAdmin, setEditingAdmin] = useState<EventAdministrator | null>(null)
  const [menuOpenId, setMenuOpenId] = useState<number | null>(null)
  
  // Permission checks
  const canAddEventAdmin = usePermission('add_eventadministrator')
  const canChangeEventAdmin = usePermission('change_eventadministrator')
  const canDeleteEventAdmin = usePermission('delete_eventadministrator')
  
  // Queries
  const { data: adminsData, isLoading: adminsLoading } = useEventAdministrators(eventId)
  const deleteAdmin = useDeleteEventAdministrator()
  
  const administrators = adminsData?.results || []
  
  const getInitials = (admin: EventAdministrator) => {
    const user = admin.user
    if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
    }
    return user.email?.[0]?.toUpperCase() || '?'
  }
  
  const getDisplayName = (admin: EventAdministrator) => {
    const user = admin.user
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`
    }
    return user.email || 'Unknown User'
  }
  
  const formatGroupName = (name: string) => {
    // Keep "event_" prefix but format nicely: "event_admin" -> "Event Admin"
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }
  
  const handleDelete = async (adminId: number) => {
    if (!confirm('Are you sure you want to remove this administrator?')) return
    
    try {
      await deleteAdmin.mutateAsync({ adminId, eventId })
    } catch (error) {
      console.error('Failed to delete administrator:', error)
      alert('Failed to remove administrator. Please try again.')
    }
  }
  
  return (
    <div className={`space-y-8`}>
      <div className="bg-white dark:bg-neutral-900 rounded-lg border border-neutral-200 dark:border-neutral-800 p-6">
          <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Event Access</h2>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
              Manage access to this event
            </p>
          </div>
          {canAddEventAdmin && (
            <Button type="button" onClick={() => setShowAddModal(true)}>
              <UserPlus className="w-4 h-4 mr-2" />
              Add Admin
            </Button>
          )}
        </div>

        {adminsLoading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-neutral-200 dark:bg-neutral-800 rounded" />
            ))}
          </div>
        ) : administrators.length === 0 ? (
          /* Empty State */
          <div className="text-center py-12">
            <svg className="w-12 h-12 mx-auto text-neutral-400 dark:text-neutral-600 mb-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="8.5" cy="7" r="4"></circle>
              <line x1="20" y1="8" x2="20" y2="14"></line>
              <line x1="23" y1="11" x2="17" y2="11"></line>
            </svg>
            <p className="text-neutral-600 dark:text-neutral-400 text-sm">
              No event administrators yet
            </p>
            <p className="text-neutral-500 dark:text-neutral-500 text-xs mt-1">
              Click "Add Admin" to grant access to this event
            </p>
          </div>
        ) : (
          /* Administrators List */
          <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg divide-y divide-neutral-200 dark:divide-neutral-800">
            {administrators.map((admin: EventAdministrator) => (
              <div key={admin.id} className="p-4 hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors">
                <div className="flex items-center justify-between">
                  {/* User Info */}
                  <div className="flex items-center flex-1 min-w-0">
                    <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-400 font-semibold shrink-0">
                      {getInitials(admin)}
                    </div>
                    <div className="ml-3 flex-1 min-w-0">
                      <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100 truncate">
                        {getDisplayName(admin)}
                      </p>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                        {admin.user?.email}
                      </p>
                    </div>
                  </div>
                  
                  {/* Groups/Roles and Actions */}
                  <div className="ml-4 flex items-center gap-2 shrink-0">
                    {/* Groups badges */}
                    <div className="hidden sm:flex flex-wrap gap-1">
                      {admin.groups?.map((group: PermissionGroup) => (
                        <span
                          key={group.id}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
                        >
                          <Shield className="w-3 h-3 mr-1" />
                          {formatGroupName(group.name)}
                        </span>
                      ))}
                    </div>
                    
                    {/* Actions dropdown */}
                    {(canChangeEventAdmin || canDeleteEventAdmin) && (
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setMenuOpenId(menuOpenId === admin.id ? null : admin.id)}
                          className="p-2 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        
                        {menuOpenId === admin.id && (
                          <>
                            <div 
                              className="fixed inset-0 z-10" 
                              onClick={() => setMenuOpenId(null)}
                            />
                            <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-neutral-800 rounded-md shadow-lg border border-neutral-200 dark:border-neutral-700 z-20">
                              {canChangeEventAdmin && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingAdmin(admin)
                                    setMenuOpenId(null)
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                                >
                                  <Pencil className="w-4 h-4 mr-2" />
                                  Edit Permissions
                                </button>
                              )}
                              {canDeleteEventAdmin && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleDelete(admin.id)
                                    setMenuOpenId(null)
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Remove
                                </button>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Mobile groups display */}
                <div className="sm:hidden mt-2 flex flex-wrap gap-1">
                  {admin.groups?.map((group: PermissionGroup) => (
                    <span
                      key={group.id}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
                    >
                      <Shield className="w-3 h-3 mr-1" />
                      {formatGroupName(group.name)}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex">
          <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium mb-1">About Event Administrators</p>
            <p className="text-blue-700 dark:text-blue-300">
              Event administrators have permission to manage various aspects of this event based on their assigned groups. Use groups to control what each user can access and modify.
            </p>
          </div>
        </div>
      </div>
      
      {/* Add Admin Modal */}
      {showAddModal && (
        <AddEventAdminModal 
          eventId={eventId} 
          onClose={() => setShowAddModal(false)} 
        />
      )}
      
      {/* Edit Admin Modal */}
      {editingAdmin && (
        <EditEventAdminModal 
          eventId={eventId}
          admin={editingAdmin}
          onClose={() => setEditingAdmin(null)} 
        />
      )}
    </div>
  )
}

// Add Event Admin Modal
function AddEventAdminModal({ eventId, onClose }: { eventId: number; onClose: () => void }) {
  const [email, setEmail] = useState('')
  const [selectedGroups, setSelectedGroups] = useState<number[]>([])
  const [error, setError] = useState<string | null>(null)
  
  const { data: groupsData } = usePermissionGroups()
  const createAdmin = useCreateEventAdministrator()
  
  const groups = groupsData?.results || []
  const eventGroups = groups.filter((g: PermissionGroup) => g.name.startsWith('event_'))
  
  const formatGroupName = (name: string) => {
    // Keep "event_" prefix but format nicely: "event_admin" -> "Event Admin"
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    
    if (!email.trim()) {
      setError('Email is required')
      return
    }
    
    if (selectedGroups.length === 0) {
      setError('At least one permission group is required')
      return
    }
    
    try {
      await createAdmin.mutateAsync({
        email: email.trim(),
        event: eventId,
        groups: selectedGroups,
      })
      onClose()
    } catch (err: unknown) {
      console.error('Failed to create administrator:', err)
      
      // Try to extract error message from various error formats
      let errorMessage = 'Failed to add administrator. Please try again.'
      
      if (err instanceof Error) {
        const message = err.message
        // Check for common error patterns
        if (message.includes('already exists') || message.includes('duplicate') || message.includes('unique')) {
          errorMessage = 'This user is already an administrator for this event'
        } else if (message.includes('not found') || message.includes('No user') || message.includes('does not exist')) {
          errorMessage = 'No user found with this email address. The user must have an existing account.'
        } else if (message.includes('email')) {
          errorMessage = 'Invalid email address'
        } else if (message.includes('group') || message.includes('permission')) {
          errorMessage = 'Invalid permission group selected'
        } else if (message) {
          errorMessage = message
        }
      }
      
      setError(errorMessage)
    }
  }
  
  const toggleGroup = (groupId: number) => {
    setSelectedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    )
  }
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-800">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Add Event Administrator</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-red-800 dark:text-red-200 text-sm">
              {error}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Email Address
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              className="w-full"
            />
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Permission Groups
            </label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {eventGroups.map((group: PermissionGroup) => (
                <label
                  key={group.id}
                  className="flex items-center p-3 border border-neutral-200 dark:border-neutral-700 rounded-md cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800"
                >
                  <input
                    type="checkbox"
                    checked={selectedGroups.includes(group.id)}
                    onChange={() => toggleGroup(group.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-600 rounded"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                      {formatGroupName(group.name)}
                    </span>
                    {group.description && (
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        {group.description}
                      </p>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" isLoading={createAdmin.isPending}>
              Add Administrator
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Edit Event Admin Modal
function EditEventAdminModal({ eventId, admin, onClose }: { eventId: number; admin: EventAdministrator; onClose: () => void }) {
  const [selectedGroups, setSelectedGroups] = useState<number[]>(
    admin.groups?.map(g => g.id) || []
  )
  const [error, setError] = useState<string | null>(null)
  
  const { data: groupsData } = usePermissionGroups()
  const updateAdmin = useUpdateEventAdministrator()
  
  const groups = groupsData?.results || []
  const eventGroups = groups.filter((g: PermissionGroup) => g.name.startsWith('event_'))
  
  const formatGroupName = (name: string) => {
    // Keep "event_" prefix but format nicely: "event_admin" -> "Event Admin"
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }
  
  const getDisplayName = () => {
    const user = admin.user
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`
    }
    return user.email || 'Unknown User'
  }
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    
    if (selectedGroups.length === 0) {
      setError('At least one permission group is required')
      return
    }
    
    try {
      await updateAdmin.mutateAsync({
        adminId: admin.id,
        eventId,
        data: { group_ids: selectedGroups },
      })
      onClose()
    } catch (err: unknown) {
      console.error('Failed to update administrator:', err)
      
      let errorMessage = 'Failed to update permissions. Please try again.'
      if (err instanceof Error && err.message) {
        errorMessage = err.message
      }
      setError(errorMessage)
    }
  }
  
  const toggleGroup = (groupId: number) => {
    setSelectedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    )
  }
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-800">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Edit Permissions</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-red-800 dark:text-red-200 text-sm">
              {error}
            </div>
          )}
          
          <div className="flex items-center p-3 bg-neutral-50 dark:bg-neutral-800 rounded-md">
            <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-400 font-semibold">
              {admin.user.first_name?.[0]?.toUpperCase() || admin.user.email?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                {getDisplayName()}
              </p>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                {admin.user.email}
              </p>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Permission Groups
            </label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {eventGroups.map((group: PermissionGroup) => (
                <label
                  key={group.id}
                  className="flex items-center p-3 border border-neutral-200 dark:border-neutral-700 rounded-md cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800"
                >
                  <input
                    type="checkbox"
                    checked={selectedGroups.includes(group.id)}
                    onChange={() => toggleGroup(group.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-600 rounded"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                      {formatGroupName(group.name)}
                    </span>
                    {group.description && (
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        {group.description}
                      </p>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" isLoading={updateAdmin.isPending}>
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
